import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import { Loader2, Info, Key, RotateCcw } from "lucide-react";
import MultiPoolResults from "./MultiPoolResults";
import { formatML } from "../lib/odds";
import "../styles/saddlecloth.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Saddle-cloth badge class for a program number (traditional post-position colors).
// Matches the main Tester UI (pnClass + saddlecloth.css).
const pnClass = (n) => {
  const num = parseInt(n, 10);
  return `pn-badge ${num >= 1 && num <= 24 ? `pn${num}` : "pn-default"}`;
};

// Coupled-entry name cell (parity with the main Tester): one betting interest
// per program number; coupled members joined by " / " with scratched members
// struck through. The interest stays bettable as long as one member is live.
const RunnerName = ({ entry }) => {
  if (Array.isArray(entry.coupledMembers) && entry.coupledMembers.length > 1) {
    return (
      <>
        {entry.coupledMembers.map((m, idx) => (
          <span key={m.number}>
            {idx > 0 && <span className="text-slate-500"> / </span>}
            <span
              className={m.scratched ? "line-through text-slate-500" : ""}
              data-testid={`widget-runner-member-${entry.programNumber}-${m.number}`}
            >
              {m.name}
            </span>
          </span>
        ))}
      </>
    );
  }
  return <>{entry.horseName || `Horse ${entry.programNumber}`}</>;
};

// Feature flags — built for parity with the main UI. Default OFF; the admin
// can surface them per-flag via the Admin → Jobs "Widget Features" control
// (GET /api/quickpick/widget-settings).
const DEFAULT_SHOW_LATE_SIGNAL = false;   // Strong/Fade (mustUse/dontUse) cues + legend
const DEFAULT_SHOW_STRAIGHT_BETS = false; // Straight Bet Options + My Keys Only controls

// Pool labels — identical to the main UI so codes/labels match.
const POOL_LABELS = { EX: "Exacta", TRI: "Trifecta", SPR: "Superfecta" };
// Multi-vertical combo pools — split the bankroll evenly across constituents.
const POOL_COMBOS = [
  ["EX", "TRI"],
  ["EX", "SPR"],
  ["TRI", "SPR"],
  ["EX", "TRI", "SPR"],
];

export const QuickPickWidget = () => {
  // Get params from URL
  const urlParams = new URLSearchParams(window.location.search);
  const track = urlParams.get("track")?.toUpperCase() || "";
  const race = urlParams.get("race") || "";
  const clientId = urlParams.get("clientId") || "TEST001";
  const raceDate = urlParams.get("date") || "";

  const [selectedPool, setSelectedPool] = useState(() => {
    return localStorage.getItem("quickpick_pool") || "";
  });
  const [betAmount, setBetAmount] = useState(() => {
    // Load from localStorage or default to 20
    return localStorage.getItem("quickpick_betamt") || "20";
  });
  const [loading, setLoading] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [entries, setEntries] = useState([]);
  const [pools, setPools] = useState([]);
  const [poolEligibility, setPoolEligibility] = useState(null);
  const [straightBetOption, setStraightBetOption] = useState("none");
  const [myKeysOnly, setMyKeysOnly] = useState(true);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [raceInfo, setRaceInfo] = useState({ track: "", race: "", fieldSize: 0 });

  // Per-client widget config (fetched from backend by clientId).
  const [mode, setMode] = useState("simple");           // "simple" | "pro"
  const [allowCombos, setAllowCombos] = useState(true);
  const [branding, setBranding] = useState("intellitix"); // "intellitix" | "neutral"
  const [amountPresets, setAmountPresets] = useState([20, 30, 40, 50, 75, 100, 150, 200, 300, 500]);
  const [showLateSignal, setShowLateSignal] = useState(DEFAULT_SHOW_LATE_SIGNAL);
  const [showStraightBets, setShowStraightBets] = useState(DEFAULT_SHOW_STRAIGHT_BETS);
  // Simple-mode runner selection: auto (IntelliTix) vs choose-your-own.
  const [pickMode, setPickMode] = useState("auto");      // "auto" | "choose"
  // Set true when an auto-build attempt for THIS race returns "not enough data"
  // (the engine's authoritative gate — stricter than the client-side heuristic).
  // Forces the manual "Specify Your Keys" flow instead of a red error.
  const [autoBlocked, setAutoBlocked] = useState(false);
  const isKiosk = mode === "kiosk";

  useEffect(() => {
    axios
      .get(`${API}/quickpick/widget-config?clientId=${encodeURIComponent(clientId)}`)
      .then((r) => {
        if (r.data?.success) {
          setMode(r.data.mode === "pro" ? "pro" : r.data.mode === "kiosk" ? "kiosk" : "simple");
          setAllowCombos(!!r.data.allowCombos);
          setBranding(r.data.branding === "neutral" ? "neutral" : "intellitix");
          if (Array.isArray(r.data.amountPresets) && r.data.amountPresets.length) {
            setAmountPresets(r.data.amountPresets);
          }
          setShowLateSignal(!!r.data.showLateSignal);
          setShowStraightBets(!!r.data.showStraightBets);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save betAmount to localStorage when it changes (skip on kiosk — shared device)
  useEffect(() => {
    if (betAmount && !isKiosk) {
      localStorage.setItem("quickpick_betamt", betAmount);
    }
  }, [betAmount, isKiosk]);

  // Save pool to localStorage when it changes (skip on kiosk)
  useEffect(() => {
    if (selectedPool && !isKiosk) {
      localStorage.setItem("quickpick_pool", selectedPool);
    }
  }, [selectedPool, isKiosk]);

  // Pool options — matches the main UI codes/labels (singles + multi-vertical combos).
  // Driven by poolEligibility from /entries, with a fallback to offered codes.
  const poolOptions = (() => {
    const elig = poolEligibility;
    const offeredCodes = pools
      .map((p) => {
        const c = p.code?.toUpperCase();
        return c === "SUP" ? "SPR" : c;
      })
      .filter((c) => ["EX", "TRI", "SPR"].includes(c));
    const singles = ["EX", "TRI", "SPR"].filter((c) =>
      elig ? elig[c]?.show : offeredCodes.includes(c)
    );
    const opts = singles.map((c) => ({
      value: c,
      pools: [c],
      label: POOL_LABELS[c],
      requiresKey: !!(elig && elig[c]?.requiresKey),
    }));
    // Multi-vertical combos: only when every constituent pool is offered AND
    // this client allows combos.
    if (allowCombos) POOL_COMBOS.forEach((combo) => {
      if (combo.every((c) => singles.includes(c))) {
        opts.push({
          value: combo.join("+"),
          pools: combo,
          label: combo.map((c) => POOL_LABELS[c]).join(" + "),
          requiresKey: combo.some((c) => !!(elig && elig[c]?.requiresKey)),
        });
      }
    });
    return opts;
  })();

  // Strong/Fade (Late Signal) suppression — mirror main UI: when a flag appears on
  // more than 2 active runners it's noise, so don't ring them.
  const activeEntries = entries.filter((e) => !e.scratched);
  const suppressMU = activeEntries.filter((e) => e.mustUse).length > 2;
  const suppressDU = activeEntries.filter((e) => e.dontUse).length > 2;

  // Currently selected pool option (single or combo).
  const currentPoolOption = poolOptions.find((o) => o.value === selectedPool) || null;

  // Max runners the user may designate. Conform to the main app: UP TO 3 keys,
  // then the engine fills the remaining runners per the rule set. Respect a
  // smaller pool cap if a combo leg allows fewer than 3.
  const maxKeys = (() => {
    if (!currentPoolOption || !poolEligibility) return 3;
    const caps = currentPoolOption.pools
      .map((c) => poolEligibility[c]?.maxRunners)
      .filter((n) => typeof n === "number" && n > 0);
    const poolCap = caps.length ? Math.min(...caps) : 3;
    return Math.min(3, poolCap);
  })();

  // Kiosk "makes sense" gating — smallest sensible wager ($) for a pool option.
  // Combos split evenly, so they need (#legs × the richest leg's minimum).
  const optionMinWager = (opt) => {
    if (!poolEligibility) return 0;
    const legMins = opt.pools.map((c) => poolEligibility[c]?.minWager || 0);
    if (legMins.some((m) => !m)) return 0; // unknown -> don't gate
    return opt.pools.length * Math.max(...legMins);
  };
  const amtNum = parseInt(betAmount, 10) || 0;
  const isAffordable = (opt) => !isKiosk || amtNum >= optionMinWager(opt);
  // Cheapest wager available this race (kiosk: amounts below this buy nothing).
  const minAnyWager = (() => {
    if (!isKiosk || !poolEligibility || poolOptions.length === 0) return 0;
    const mins = poolOptions.map(optionMinWager).filter((m) => m > 0);
    return mins.length ? Math.min(...mins) : 0;
  })();

  // Branded vs neutral copy (client-configurable).
  const COPY = branding === "neutral"
    ? {
        auto: "Auto Pick",
        choose: "Choose My Own",
        blurb: "Let our algorithm build your ticket from real-time data.",
        cta: "Create My Tickets",
      }
    : {
        auto: "Use IntelliTix™",
        choose: "I Want to Choose",
        blurb: "IntelliTix™ uses AI-assisted algorithms with real-time data to give you the best chance of success. See you in the Winner's Circle!",
        cta: "Create My Tickets",
      };

  // "No usable data" gate — mirror the main Tester: when most runners lack Beyer
  // figures AND there are no usable projected odds AND no usable pool rankings,
  // require the user to designate keys instead of auto-selecting.
  const insufficientDataRequiresKey = (() => {
    const active = entries.filter((e) => !e.scratched);
    if (active.length === 0) return false;
    const beyerZero = active.filter((e) => !e.avgBeyer2 || e.avgBeyer2 === 0).length;
    if (beyerZero / active.length <= 0.6) return false;
    const noProjOdds = active.every((e) => !e.projOdds || e.projOdds <= 0);
    if (!noProjOdds) return false;
    const poolUsable = (field) => {
      const vals = active.map((e) => e[field]);
      const allValid = vals.every((v) => v !== -99 && v != null);
      const hasVariance = new Set(vals).size > 1;
      const notAllZero = !vals.every((v) => v === 0);
      return allValid && hasVariance && notAllZero;
    };
    const anyPoolUsable = ["p3Score", "p4Score", "p5Score", "dblScore"].some(poolUsable);
    return !anyPoolUsable;
  })();

  // Block auto-generate when the selected pool/combo needs a key but none chosen
  // (parity with the main Tester — prevents a combo leg silently failing).
  const selectionRequiresKey =
    (!!currentPoolOption?.requiresKey || insufficientDataRequiresKey) &&
    selectedKeys.length === 0;

  // Bet amount must be a whole number >= $5 (parity with main Tester).
  const betAmountValid = (parseInt(betAmount, 10) || 0) >= 5;

  // Auto (IntelliTix) can't build tickets for this pool/combo under the current
  // rule set — force the user to designate keys instead.
  const autoUnavailable = !!currentPoolOption?.requiresKey || insufficientDataRequiresKey || autoBlocked;
  // First constituent pool that blocks auto-generation (for the disabled label).
  const firstBlockingPoolLabel = (() => {
    if (!currentPoolOption) return null;
    if (poolEligibility) {
      for (const c of currentPoolOption.pools) {
        if (poolEligibility[c]?.requiresKey) return POOL_LABELS[c];
      }
    }
    return currentPoolOption.label;
  })();

  // Auto-select the first available pool (parity with main UI) when none is chosen
  // or when a saved pool isn't offered for this race.
  useEffect(() => {
    if (poolOptions.length === 0) return;
    const valid = poolOptions.some((o) => o.value === selectedPool);
    if (!valid) setSelectedPool(poolOptions[0].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolEligibility, pools]);

  // Clear generated tickets whenever any ticket-affecting input changes
  // (pool, runner choice, key selections, or bet amount).
  useEffect(() => {
    setResult(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPool, selectedKeys, betAmount, pickMode]);

  // Force "I Want to Choose" when IntelliTix can't auto-build for this pool.
  useEffect(() => {
    if (autoUnavailable && pickMode === "auto") setPickMode("choose");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoUnavailable]);

  // Kiosk: reset to a clean slate for the next customer (drops persistence).
  const resetAll = () => {
    setSelectedKeys([]);
    setResult(null);
    setError(null);
    setStraightBetOption("none");
    setPickMode("auto");
    setSelectedPool(poolOptions[0]?.value || "");
    setBetAmount(String(amountPresets[0] || 5));
  };
  const resetRef = useRef(resetAll);
  resetRef.current = resetAll;

  // On entering kiosk mode, start fresh (no carried-over amount/pool).
  useEffect(() => {
    if (mode === "kiosk") {
      setBetAmount(String(amountPresets[0] || 5));
      setSelectedKeys([]);
      setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Kiosk: auto-clear after 60s of no interaction (unattended-kiosk hygiene).
  useEffect(() => {
    if (!isKiosk) return;
    let timer;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(() => resetRef.current(), 60000);
    };
    schedule();
    window.addEventListener("pointerdown", schedule);
    window.addEventListener("keydown", schedule);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pointerdown", schedule);
      window.removeEventListener("keydown", schedule);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKiosk]);

  // Kiosk: bump the amount up to the lowest viable preset when the current
  // amount can't fund ANY wager this race.
  useEffect(() => {
    if (!isKiosk || minAnyWager <= 0) return;
    if ((parseInt(betAmount, 10) || 0) < minAnyWager) {
      const viable = amountPresets.filter((a) => a >= minAnyWager);
      if (viable.length) setBetAmount(String(Math.min(...viable)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minAnyWager, isKiosk]);

  // Kiosk: if the selected wager becomes unaffordable after an amount change,
  // switch to the first affordable wager.
  useEffect(() => {
    if (!isKiosk) return;
    const cur = poolOptions.find((o) => o.value === selectedPool);
    if (cur && !isAffordable(cur)) {
      const next =
        poolOptions.find((o) => o.pools.length === 1 && isAffordable(o)) ||
        poolOptions.find((o) => isAffordable(o));
      if (next) setSelectedPool(next.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betAmount, poolEligibility, isKiosk]);

  // Normalize single + multi (combo) results into uniform pool groups for rendering.
  const isMultiResult = result?.multi === true;
  const firstData = isMultiResult
    ? result.pools?.find((p) => p.data?.success)?.data
    : result;
  const grandTotal = isMultiResult
    ? (result.pools || []).reduce((s, p) => s + (p.data?.totalWager || 0), 0)
    : result?.totalWager || 0;
  const resultGroups = !result?.success
    ? []
    : isMultiResult
    ? (result.pools || [])
        .filter((p) => p.data?.success)
        .map((p) => ({
          label: POOL_LABELS[p.code] || p.code,
          tickets: p.data.tickets || [],
          total: p.data.totalWager || 0,
        }))
    : [
        {
          label: POOL_LABELS[selectedPool] || selectedPool,
          tickets: result.tickets || [],
          total: result.totalWager || 0,
        },
      ];
  // Total ticket count across all rendered pools (single or combo).
  const totalTicketCount = resultGroups.reduce((s, g) => s + g.tickets.length, 0);

  useEffect(() => {
    if (track && race) {
      fetchEntries();
    } else {
      setError("Missing track or race parameter");
      setLoadingEntries(false);
    }
  }, []);

  const fetchEntries = async () => {
    setLoadingEntries(true);
    setError(null);
    try {
      let url = `${API}/quickpick/entries?track=${track}&race=${race}&clientId=${encodeURIComponent(clientId)}`;
      if (raceDate) url += `&date=${raceDate}`;
      
      const response = await axios.get(url);
      if (response.data.success) {
        setEntries(response.data.entries || []);
        setPools(response.data.availablePools || []);
        setPoolEligibility(response.data.poolEligibility || null);

        const activeCount = (response.data.entries || []).filter(e => !e.scratched).length;
        setRaceInfo({ track, race, fieldSize: activeCount });
        setAutoBlocked(false);  // new race data — re-allow auto until proven otherwise
      } else {
        setError(response.data.message || "Failed to fetch race data");
      }
    } catch (e) {
      setError("Failed to connect to server");
      console.error(e);
    } finally {
      setLoadingEntries(false);
    }
  };

  const toggleKey = (programNumber) => {
    setSelectedKeys((prev) => {
      if (prev.includes(programNumber)) {
        return prev.filter((k) => k !== programNumber);
      } else {
        if (prev.length >= maxKeys) {
          return prev;
        }
        return [...prev, programNumber];
      }
    });
  };

  const generateTickets = async (forceAuto = false) => {
    if (!selectedPool || !betAmount) {
      setError("Please select pool and enter bet amount");
      return;
    }
    if ((parseInt(betAmount, 10) || 0) < 5) {
      setError("Minimum bet amount is $5");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const isCombo = selectedPool.includes("+");
      // The engine's authoritative "can't auto-rank this race" signal.
      const needsKeys = (msg) =>
        /specify your own keys|designate your own keys|not enough data/i.test(msg || "");
      const fallbackToManual = () => {
        setAutoBlocked(true);
        setPickMode("choose");
        setSelectedKeys([]);
        setResult(null);
        setError(null);
      };
      const poolsToRun = isCombo ? selectedPool.split("+") : [selectedPool];
      const totalBet = parseInt(betAmount);
      const splitEvenly = (total, n) => {
        const base = Math.floor(total / n);
        const rem = total - base * n;
        return Array.from({ length: n }, (_, i) => base + (i < rem ? 1 : 0));
      };
      const splitAmts = splitEvenly(totalBet, poolsToRun.length);

      const basePayload = {
        clientId,
        track,
        raceNumber: parseInt(race),
        myKeysOnly: myKeysOnly,
        straightBetOption: straightBetOption,
      };
      if (raceDate) basePayload.raceDate = raceDate;
      const keysForRun = forceAuto ? [] : selectedKeys;
      if (keysForRun.length > 0) basePayload.clientRunners = keysForRun.join("-");

      // Fire-and-forget submission log (widget only). One record per Submit click;
      // combos carry each leg's request+response. Never blocks or breaks the UI.
      const logSubmission = (legs, overallSuccess) => {
        try {
          axios.post(`${API}/quickpick/widget-submission`, {
            clientId, track, race: parseInt(race), raceDate: raceDate || null,
            mode, branding,
            pool: selectedPool,
            betAmount: totalBet,
            pickMode: forceAuto ? "auto" : pickMode,
            selectedKeys: keysForRun,
            straightBetOption, myKeysOnly,
            ruleSet: legs[0]?.response?.ruleSet || null,
            overallSuccess,
            legs,
            pageUrl: window.location.href,
            referrer: document.referrer || null,
          }).catch(() => {});
        } catch (_) { /* logging must never affect the widget */ }
      };

      if (isCombo) {
        // Run each vertical with its split amount (parity with main UI combo path).
        const legPayloads = poolsToRun.map((p, i) => ({
          ...basePayload, pool: p, betAmt: splitAmts[i], straightBetOption: "none",
        }));
        const responses = await Promise.all(
          legPayloads.map((payload) => axios.post(`${API}/quickpick`, payload))
        );
        const poolResults = poolsToRun.map((p, i) => ({
          code: p,
          betAmt: splitAmts[i],
          data: responses[i].data,
        }));
        logSubmission(
          poolResults.map((pr, i) => ({
            pool: pr.code, betAmt: pr.betAmt, request: legPayloads[i], response: pr.data,
          })),
          poolResults.some((pr) => pr.data.success)
        );
        const failed = poolResults.filter((p) => !p.data.success);
        if (failed.length === poolsToRun.length) {
          if (forceAuto && needsKeys(failed[0].data.message)) {
            fallbackToManual();
          } else {
            setError(failed[0].data.message || "No tickets created");
            setResult(null);
          }
        } else {
          setResult({ success: true, multi: true, pools: poolResults });
        }
      } else {
        const singlePayload = { ...basePayload, pool: selectedPool, betAmt: totalBet };
        const response = await axios.post(`${API}/quickpick`, singlePayload);
        logSubmission(
          [{ pool: selectedPool, betAmt: totalBet, request: singlePayload, response: response.data }],
          !!response.data.success
        );
        if (!response.data.success && forceAuto && needsKeys(response.data.message)) {
          fallbackToManual();
        } else {
          setResult(response.data);
          if (!response.data.success) {
            setError(response.data.message);
          }
        }
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to generate tickets");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (!track || !race) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 p-4 flex items-center justify-center">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6">
            <p className="text-red-400">Missing required parameters.</p>
            <p className="text-slate-400 text-sm mt-2">
              Usage: ?track=DEL&race=4&clientId=YOUR_ID
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Shared tickets renderer (used by both pro + simple layouts).
  const renderTicketsBody = () => {
    if (!result?.success) {
      return (
        <div className="text-slate-500 text-center py-12">
          Select pool and create tickets
        </div>
      );
    }
    if (isMultiResult) return <MultiPoolResults pools={result.pools} showPnl={false} />;
    return (
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="bg-slate-800 p-2 rounded shrink-0">
            <div className="text-slate-400 text-xs">Keys</div>
            <div className="text-sm font-bold text-amber-400 whitespace-nowrap">
              {firstData?.keysUsed?.join(", ") || "-"}
            </div>
          </div>
          <div className="bg-slate-800 p-2 rounded flex-1 min-w-0">
            <div className="text-slate-400 text-xs">All Runners</div>
            <div className="text-sm font-bold text-slate-100">
              {firstData?.runnersUsed?.join(", ") || "-"}
            </div>
          </div>
        </div>
        {resultGroups.map((group, gi) => (
          <div
            key={gi}
            className={`space-y-1 ${gi > 0 ? "pt-3 mt-3 border-t border-slate-700" : ""}`}
          >
            {resultGroups.length > 1 && (
              <div className="flex items-center justify-between text-xs px-1">
                <span className="font-bold uppercase tracking-wide text-emerald-300">
                  {group.label}
                </span>
                <span className="text-slate-400">${group.total.toFixed(2)}</span>
              </div>
            )}
            <div
              className="border border-slate-700 rounded p-2 flex flex-wrap gap-x-2 gap-y-1.5"
              data-testid={`widget-combos-${gi}`}
            >
              {group.tickets.map((ticket, idx) => (
                <span key={idx} className="font-mono text-sm whitespace-nowrap">
                  <span className="text-slate-100">{ticket.combo}</span>
                  <span className="text-slate-400">, </span>
                  <span className="text-emerald-400">${ticket.amount.toFixed(2)}</span>
                  {idx < group.tickets.length - 1 && (
                    <span className="text-slate-600 ml-2 select-none">|</span>
                  )}
                </span>
              ))}
            </div>
            <div className="text-xs text-slate-500 text-right">
              {group.tickets.length} tickets
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Compact submit (inert for now — will hand ticket JSON to the embedding client).
  // Rendered inline in the "My Tickets" header to save vertical space.
  const renderSubmit = () =>
    result?.success && totalTicketCount > 0 ? (
      <Button
        size="sm"
        onClick={(e) => e.preventDefault()}
        className="h-10 px-6 bg-gradient-to-r from-sky-500 to-cyan-400 hover:from-sky-400 hover:to-cyan-300 text-white font-bold text-sm rounded-lg shadow-lg shadow-sky-500/40 ring-1 ring-sky-300/50 transition-transform hover:scale-[1.03] shrink-0"
        data-testid="widget-submit-btn"
        title="Coming soon"
      >
        Submit {totalTicketCount} {totalTicketCount === 1 ? "Ticket" : "Tickets"} · ${grandTotal.toFixed(2)}
      </Button>
    ) : null;


  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-3">
      <div className={`${mode === "simple" || mode === "kiosk" ? "max-w-md" : "max-w-4xl"} mx-auto space-y-3`}>
        {/* Header */}
        <div className="flex items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold text-emerald-400">
              QuickPick - {track} Race {race}
            </h1>
          </div>
          {isKiosk && (
            <Button
              onClick={resetAll}
              variant="outline"
              className="h-12 px-4 text-base border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700 shrink-0"
              data-testid="widget-start-over-btn"
            >
              <RotateCcw className="h-4 w-4 mr-2" /> Start Over
            </Button>
          )}
        </div>

        {loadingEntries ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
            <span className="ml-2 text-slate-400">Loading race data...</span>
          </div>
        ) : error && entries.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6">
              <p className="text-red-400">{error}</p>
            </CardContent>
          </Card>
        ) : mode === "simple" || mode === "kiosk" ? (
          /* ---------- SIMPLE / KIOSK single-column flow ---------- */
          <div className="space-y-3" data-testid="widget-simple">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4 space-y-5">
                {/* Step 1 — Amount */}
                <div>
                  <Label className="text-slate-300 text-sm font-semibold">1 · Set Amount</Label>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {amountPresets.map((amt) => {
                      const amtDisabled = isKiosk && minAnyWager > 0 && amt < minAnyWager;
                      return (
                      <Button
                        key={amt}
                        size="sm"
                        disabled={amtDisabled}
                        onClick={() => setBetAmount(amt.toString())}
                        className={`${isKiosk ? "h-14 px-5 text-base" : "h-10 px-4 text-sm"} rounded-full font-semibold transition-colors ${
                          amtDisabled
                            ? "bg-slate-800/40 text-slate-600 cursor-not-allowed"
                            : betAmount === amt.toString()
                            ? "bg-emerald-600 text-white"
                            : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                        }`}
                        data-testid={`widget-amt-${amt}`}
                      >
                        ${amt}
                      </Button>
                      );
                    })}
                    {!isKiosk && (
                    <div className="flex flex-col items-center">
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={betAmount}
                        onChange={(e) => setBetAmount(e.target.value.replace(/[^0-9]/g, ""))}
                        onFocus={(e) => e.target.select()}
                        className={`h-10 w-24 text-right rounded-full bg-orange-500 text-white font-semibold placeholder:text-white/70 ${
                          betAmount && !betAmountValid
                            ? "border-red-500 focus-visible:ring-red-500"
                            : "border-orange-400 focus-visible:ring-orange-300"
                        }`}
                        placeholder="$"
                        data-testid="widget-amt-custom"
                      />
                      <span className="text-xs text-slate-400 mt-1">Custom</span>
                    </div>
                    )}
                  </div>
                  {betAmount && !betAmountValid && (
                    <p className="text-xs text-red-400 mt-1">Minimum bet amount is $5</p>
                  )}
                </div>

                {/* Step 2 — Wager */}
                <div>
                  <Label className="text-slate-300 text-sm font-semibold">2 · Select Wager</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {poolOptions.filter((o) => o.pools.length === 1).map((opt) => {
                      const aff = isAffordable(opt);
                      return (
                      <Button
                        key={opt.value}
                        disabled={isKiosk && !aff}
                        onClick={() => setSelectedPool(opt.value)}
                        className={`${isKiosk ? "h-14 px-5 text-base" : "h-10 px-4 text-sm"} rounded-lg font-semibold transition-colors ${
                          isKiosk && !aff
                            ? "bg-slate-800/40 text-slate-600 cursor-not-allowed"
                            : selectedPool === opt.value
                            ? "bg-emerald-600 text-white"
                            : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                        }`}
                        data-testid={`widget-wager-${opt.value}`}
                      >
                        <span className="flex flex-col items-center leading-tight">
                          <span>{opt.label}</span>
                          {isKiosk && !aff && (
                            <span className="text-[10px] opacity-80">min ${optionMinWager(opt)}</span>
                          )}
                        </span>
                      </Button>
                      );
                    })}
                  </div>
                  {allowCombos && poolOptions.some((o) => o.pools.length > 1) && (
                    <>
                      <div className="text-xs text-slate-500 mt-3 mb-1">Combos (total wager split evenly across pools)</div>
                      <div className="flex flex-wrap gap-2">
                        {poolOptions.filter((o) => o.pools.length > 1).map((opt) => {
                          const aff = isAffordable(opt);
                          return (
                          <Button
                            key={opt.value}
                            size="sm"
                            disabled={isKiosk && !aff}
                            onClick={() => setSelectedPool(opt.value)}
                            className={`${isKiosk ? "h-12 px-3 text-sm" : "h-9 px-3 text-xs"} rounded-lg font-medium transition-colors ${
                              isKiosk && !aff
                                ? "bg-slate-800/40 text-slate-600 cursor-not-allowed"
                                : selectedPool === opt.value
                                ? "bg-sky-600 text-white"
                                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                            }`}
                            data-testid={`widget-wager-${opt.value}`}
                          >
                            <span className="flex flex-col items-center leading-tight">
                              <span>{opt.label}</span>
                              {isKiosk && !aff && (
                                <span className="text-[10px] opacity-80">min ${optionMinWager(opt)}</span>
                              )}
                            </span>
                          </Button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>

                {/* Step 3 — Runners */}
                <div>
                  <Label className="text-slate-300 text-sm font-semibold">3 · Select Runners</Label>
                  {autoUnavailable ? (
                    <Button
                      onClick={() => setPickMode("choose")}
                      className={`w-full flex-col leading-tight mt-2 ${isKiosk ? "h-16 text-base" : "h-12 text-sm"} font-semibold transition-colors ${
                        pickMode === "choose"
                          ? "bg-amber-600 text-white"
                          : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                      }`}
                      data-testid="widget-mode-choose"
                    >
                      <span>Specify Your Keys</span>
                      <span className="text-[10px] font-normal opacity-90">(Auto Gen Unavailable)</span>
                    </Button>
                  ) : (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Button
                      onClick={() => { setPickMode("auto"); setSelectedKeys([]); generateTickets(true); }}
                      disabled={loading}
                      className={`${isKiosk ? "h-16 text-base" : "h-11 text-sm"} font-semibold transition-colors ${
                        pickMode === "auto"
                          ? "bg-emerald-600 text-white"
                          : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                      }`}
                      data-testid="widget-mode-auto"
                    >
                      {loading && pickMode === "auto" ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" /> Creating…
                        </span>
                      ) : (
                        COPY.auto
                      )}
                    </Button>
                    <Button
                      onClick={() => setPickMode("choose")}
                      className={`${isKiosk ? "h-16 text-base" : "h-11 text-sm"} font-semibold transition-colors ${
                        pickMode === "choose"
                          ? "bg-amber-600 text-white"
                          : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                      }`}
                      data-testid="widget-mode-choose"
                    >
                      {COPY.choose}
                    </Button>
                  </div>
                  )}

                  {pickMode === "auto" ? (
                    <p className="text-xs text-slate-400 mt-2" data-testid="widget-auto-blurb">{COPY.blurb}</p>
                  ) : (
                    <div className="mt-3 space-y-2">
                      {autoBlocked && (
                        <p className="text-xs text-amber-400/90" data-testid="widget-auto-fallback-note">
                          Not enough handicapping data for IntelliTix to auto-build this race — pick your own key horses below.
                        </p>
                      )}
                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <span>Tap a horse to select (up to {maxKeys})</span>
                        <span>{selectedKeys.length}/{maxKeys}</span>
                      </div>
                      <div className="border border-slate-700 rounded">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-slate-700 bg-slate-800">
                              <TableHead className="text-slate-300 w-8 p-2">Key</TableHead>
                              <TableHead className="text-slate-300 w-8 p-2">#</TableHead>
                              <TableHead className="text-slate-300 p-2">Name</TableHead>
                              <TableHead className="text-slate-300 text-right p-2">ML</TableHead>
                              <TableHead className="text-slate-300 text-right p-2">Live</TableHead>
                              <TableHead className="text-slate-300 text-right p-2">Proj</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {entries.map((entry) => {
                              const isScratched = entry.scratched;
                              const isSelected = selectedKeys.includes(entry.programNumber);
                              const atCap = !isSelected && selectedKeys.length >= maxKeys;
                              return (
                                <TableRow
                                  key={entry.programNumber}
                                  className={`border-slate-700 ${
                                    isScratched
                                      ? "opacity-40 cursor-not-allowed"
                                      : atCap
                                      ? "opacity-60 cursor-not-allowed"
                                      : `cursor-pointer hover:bg-slate-800 ${isSelected ? "bg-amber-900/30" : ""}`
                                  }`}
                                  onClick={() => { if (!isScratched && !atCap) toggleKey(entry.programNumber); }}
                                  data-testid={`widget-runner-${entry.programNumber}`}
                                >
                                  <TableCell className="p-2" onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                      checked={isSelected}
                                      disabled={isScratched || atCap}
                                      onCheckedChange={() => { if (!isScratched && !atCap) toggleKey(entry.programNumber); }}
                                      className={`border-slate-600 data-[state=checked]:bg-amber-600 ${isKiosk ? "h-6 w-6" : ""}`}
                                      data-testid={`widget-runner-key-${entry.programNumber}`}
                                    />
                                  </TableCell>
                                  <TableCell className="p-2">
                                    <span className={`${pnClass(entry.programNumber)} ${isScratched ? "opacity-50 line-through" : ""}`}>
                                      {entry.programNumber}
                                    </span>
                                  </TableCell>
                                  <TableCell className={`p-2 text-sm ${isScratched ? "text-slate-500" : isSelected ? "text-amber-300 font-semibold" : "text-slate-300"}`}>
                                    <RunnerName entry={entry} />
                                    {isScratched && <span className="ml-1 text-red-400 text-xs">(SCR)</span>}
                                  </TableCell>
                                  <TableCell className={`text-right p-2 ${isScratched ? "text-slate-500" : "text-slate-300"}`}>
                                    {formatML(entry.mlOdds)}
                                  </TableCell>
                                  <TableCell className={`text-right p-2 ${isScratched ? "text-slate-500" : "text-slate-100"}`}>
                                    {entry.xmlOdds > 0 ? entry.xmlOdds.toFixed(1) : "-"}
                                  </TableCell>
                                  <TableCell className={`text-right p-2 ${isScratched ? "text-slate-500" : "text-emerald-400"}`}>
                                    {entry.projOdds > 0 ? entry.projOdds.toFixed(1) : "n/a"}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                      {selectedKeys.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-amber-400">
                          <Info className="h-3 w-3" />
                          {selectedKeys.length === 1
                            ? "Your key must finish 1st or 2nd"
                            : "One of your keys must win"}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* CTA — only for the "choose your own" flow; IntelliTix auto-generates on click */}
                {pickMode === "choose" && (
                <Button
                  onClick={() => generateTickets()}
                  disabled={loading || !selectedPool || selectionRequiresKey || !betAmountValid}
                  className={`w-full ${isKiosk ? "h-16 text-lg" : "h-12 text-base"} font-bold text-white ${
                    selectionRequiresKey
                      ? "bg-amber-700 hover:bg-amber-700 cursor-not-allowed"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                  data-testid="widget-create-btn"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Creating…
                    </span>
                  ) : selectionRequiresKey ? (
                    <span className="flex items-center justify-center gap-2">
                      <Key className="h-4 w-4" /> Choose your runners to continue
                    </span>
                  ) : (
                    COPY.cta
                  )}
                </Button>
                )}

                {error && (
                  <div className="p-2 bg-red-900/50 border border-red-700 rounded text-red-300 text-sm">
                    {error}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Results */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-emerald-400 text-base">My Tickets</CardTitle>
                  {renderSubmit()}
                </div>
              </CardHeader>
              <CardContent>
                {renderTicketsBody()}
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Left: Runners + Controls */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-emerald-400 text-base">
                  Select Keys (optional)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Runners Table */}
                <div className="border border-slate-700 rounded">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700 bg-slate-800">
                        <TableHead className="text-slate-300 w-8 p-2">Key</TableHead>
                        <TableHead className="text-slate-300 w-8 p-2">#</TableHead>
                        <TableHead className="text-slate-300 p-2">Name</TableHead>
                        <TableHead className="text-slate-300 text-right p-2">Live</TableHead>
                        <TableHead className="text-slate-300 text-right p-2">Proj</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry) => {
                        const isScratched = entry.scratched;
                        const isSelected = selectedKeys.includes(entry.programNumber);
                        const isStrong = showLateSignal && !isScratched && entry.mustUse && !suppressMU;
                        const isFade = showLateSignal && !isScratched && entry.dontUse && !suppressDU;
                        const handleToggle = () => {
                          if (!isScratched) toggleKey(entry.programNumber);
                        };
                        return (
                          <TableRow
                            key={entry.programNumber}
                            className={`border-slate-700 ${
                              isScratched
                                ? "opacity-40 cursor-not-allowed"
                                : `cursor-pointer hover:bg-slate-800 ${isSelected ? "bg-amber-900/30" : ""} ${
                                    isStrong ? "ring-1 ring-inset ring-emerald-500/70" : ""
                                  } ${isFade ? "ring-1 ring-inset ring-amber-500/70" : ""}`
                            }`}
                            onClick={handleToggle}
                          >
                            <TableCell className="p-2" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={isSelected}
                                disabled={isScratched}
                                onCheckedChange={handleToggle}
                                className="border-slate-600 data-[state=checked]:bg-amber-600"
                              />
                            </TableCell>
                            <TableCell className="p-2">
                              <span className={`${pnClass(entry.programNumber)} ${isScratched ? "opacity-50 line-through" : ""}`}>
                                {entry.programNumber}
                              </span>
                            </TableCell>
                            <TableCell className={`p-2 text-sm ${isScratched ? "text-slate-500" : "text-slate-300"}`}>
                              <RunnerName entry={entry} />
                              {isScratched && <span className="ml-1 text-red-400 text-xs">(SCR)</span>}
                            </TableCell>
                            <TableCell className={`text-right p-2 ${isScratched ? "text-slate-500" : "text-slate-100"}`}>
                              {entry.xmlOdds > 0 ? entry.xmlOdds.toFixed(1) : "-"}
                            </TableCell>
                            <TableCell className={`text-right p-2 ${isScratched ? "text-slate-500" : "text-emerald-400"}`}>
                              {entry.projOdds > 0 ? entry.projOdds.toFixed(1) : "n/a"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Late Signal legend — Strong / Fade (hidden until enabled) */}
                {showLateSignal && (
                  <div className="flex flex-wrap items-center gap-2" data-testid="widget-flag-legend">
                    <span className="inline-flex items-center whitespace-nowrap rounded border border-emerald-500/80 bg-emerald-500/15 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-emerald-300">
                      Late Signal: Strong
                    </span>
                    <span className="inline-flex items-center whitespace-nowrap rounded border border-amber-500/80 bg-amber-500/15 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-amber-300">
                      Late Signal: Fade
                    </span>
                  </div>
                )}

                {/* Selected Keys Display */}
                {selectedKeys.length > 0 && (
                  <div className="p-2 bg-slate-800 rounded space-y-1">
                    <div>
                      <span className="text-slate-400 text-sm">Your Keys: </span>
                      {selectedKeys.map((k) => (
                        <Badge key={k} className="mr-1 bg-amber-600 text-white text-xs">
                          #{k}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-amber-400">
                      <Info className="h-3 w-3" />
                      {selectedKeys.length === 1 
                        ? "Your Key Must Run 1st or 2nd"
                        : "One of Your Keys Must Win"
                      }
                    </div>
                  </div>
                )}

                {/* Pool */}
                <div>
                  <Label className="text-slate-300 text-xs">Pool</Label>
                  <Select value={selectedPool} onValueChange={setSelectedPool}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100 h-9" data-testid="widget-pool-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {poolOptions.map((opt) => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          className="text-slate-100 focus:bg-emerald-600 focus:text-white"
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Amount */}
                <div>
                  <Label className="text-slate-300 text-xs">
                    Amount <span className="text-slate-500">(enter custom amount)</span>
                  </Label>
                  <div className="flex gap-2">
                    <div className="flex flex-col items-start">
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={betAmount}
                        onChange={(e) => setBetAmount(e.target.value.replace(/[^0-9]/g, ""))}
                        onFocus={(e) => e.target.select()}
                        onClick={(e) => e.target.select()}
                        className={`text-slate-100 h-9 w-20 text-right bg-amber-400/15 ${
                          betAmount && !betAmountValid
                            ? "border-red-500 focus-visible:ring-red-500"
                            : "border-amber-400/40 focus-visible:ring-amber-400/50"
                        }`}
                        placeholder="20"
                        data-testid="widget-bet-amount-input"
                      />
                      <span className="text-xs text-slate-400 mt-1">Custom</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {[20, 30, 50, 75, 100, 200, 300, 500].map((amt) => (
                        <Button
                          key={amt}
                          variant="outline"
                          size="sm"
                          onClick={() => setBetAmount(amt.toString())}
                          className={`h-9 px-2 text-xs border-slate-600 ${
                            betAmount === amt.toString()
                              ? "bg-emerald-600 text-white border-emerald-600"
                              : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                          }`}
                        >
                          ${amt}
                        </Button>
                      ))}
                    </div>
                  </div>
                  {betAmount && !betAmountValid && (
                    <p className="text-xs text-red-400 mt-1" data-testid="widget-bet-amount-error">
                      Minimum bet amount is $5
                    </p>
                  )}
                </div>

                {/* Straight Bet Options + My Keys Only (built for parity; hidden until enabled) */}
                {showStraightBets && (
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-xs">Straight Bet Options</Label>
                    <div className="flex items-center gap-3">
                      <Select value={straightBetOption} onValueChange={setStraightBetOption}>
                        <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-100 h-9 w-40" data-testid="widget-straight-bet-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="none" className="text-slate-100 focus:bg-emerald-600 focus:text-white">None</SelectItem>
                          <SelectItem value="plus_win" className="text-slate-100 focus:bg-emerald-600 focus:text-white">+ Win</SelectItem>
                          <SelectItem value="plus_wp" className="text-slate-100 focus:bg-emerald-600 focus:text-white">+ Win/Place</SelectItem>
                          <SelectItem value="win_only" className="text-slate-100 focus:bg-emerald-600 focus:text-white">Win Only</SelectItem>
                          <SelectItem value="wp_only" className="text-slate-100 focus:bg-emerald-600 focus:text-white">Win/Place Only</SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={myKeysOnly}
                          onCheckedChange={(c) => setMyKeysOnly(!!c)}
                          className="border-slate-600 data-[state=checked]:bg-amber-600"
                        />
                        <span className="text-xs text-slate-300">My Keys Only</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Generate Button */}
                <Button
                  onClick={() => generateTickets()}
                  disabled={loading || !selectedPool || selectionRequiresKey || !betAmountValid}
                  className={`w-full text-white ${
                    selectionRequiresKey
                      ? "bg-amber-700 hover:bg-amber-700 cursor-not-allowed"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                  data-testid="widget-generate-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : selectionRequiresKey ? (
                    <span className="flex items-center justify-center gap-2">
                      <Key className="h-4 w-4" />
                      This Wager Requires You to Designate Keys
                    </span>
                  ) : selectedKeys.length > 0 ? (
                    `Generate with Keys (${selectedKeys.join(",")})`
                  ) : (
                    "Auto Generate"
                  )}
                </Button>

                {error && (
                  <div className="p-2 bg-red-900/50 border border-red-700 rounded text-red-300 text-sm">
                    {error}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Right: Results */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-emerald-400 text-base">Tickets</CardTitle>
                  {renderSubmit()}
                </div>
              </CardHeader>
              <CardContent>
                {renderTicketsBody()}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickPickWidget;
